import { useEffect, useState } from 'preact/hooks';
import { Sheet, Avatar, Stars } from '../components/ui';
import { Cover } from '../components/Cover';
import { useBooks, useStore } from '../hooks';
import { DESTINATION_LABELS, READING_LABELS, type Copy, type ReadingStatus } from '../../shared/types';
import { setReading, deleteWork } from '../lib/actions';
import { EditWorkSheet, EditCopySheet, AddCopySheet } from './Forms';
import { CoverPicker } from './CoverPicker';
import { updateWork } from '../lib/actions';
import { toast } from '../components/toast';
import { apiJson } from '../lib/http';

const STATUS_ORDER: ReadingStatus[] = ['necitit', 'vreau', 'in_curs', 'citit'];
const COPY_STATUS: Record<string, string> = {
  acasa: 'Acasă', imprumutata: 'Împrumutată', vanduta: 'Vândută', donata: 'Donată', returnata_bibliotecii: 'Returnată bibliotecii', pierduta: 'Pierdută'
};

export function BookDetail({ workId, onClose }: { workId: string | null; onClose: () => void }) {
  const s = useStore();
  const books = useBooks();
  const b = books.find(x => x.work.id === workId);
  const [editWork, setEditWork] = useState(false);
  const [editCopy, setEditCopy] = useState<Copy | null>(null);
  const [addCopy, setAddCopy] = useState(false);
  const [history, setHistory] = useState<any[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);

  useEffect(() => { setHistory(null); setExpanded(false); }, [workId]);
  if (!workId) return null;
  if (!b) return <Sheet open onClose={onClose} title="Carte"><p class="text-ink-500">Cartea nu mai există (a fost ștearsă).</p></Sheet>;

  const me = s.me;
  const isAdmin = me?.role === 'admin';
  const my = b.myReading;
  const status = (my?.status || 'necitit') as ReadingStatus;
  const today = new Date().toISOString().slice(0, 10);

  const setStatus = (st: ReadingStatus) => {
    const fields: any = { status: st };
    if (st === 'in_curs' && !my?.started_at) fields.started_at = today;
    if (st === 'citit') { fields.finished_at = my?.finished_at || today; fields.read_year = my?.read_year || Number(today.slice(0, 4)); }
    void setReading(b.work.id, fields);
    if (st === 'citit') toast('Felicitări! Încă o carte citită 🎉');
  };

  const loadHistory = async () => {
    const r = await apiJson(`/api/history/works/${b.work.id}`).catch(() => ({ history: [] }));
    setHistory(r.history || []);
  };

  const otherReaders = b.readers.filter(r => r.member_id !== me?.id && r.status !== 'necitit');
  const synopsis = b.work.synopsis || '';
  const canDelete = isAdmin || b.work.created_by === me?.id;

  return (
    <Sheet open onClose={onClose} title="Fișa cărții">
      <div class="flex gap-4">
        <button class="relative shrink-0" onClick={() => setCoverOpen(true)} aria-label="Schimbă coperta" data-testid="change-cover">
          <Cover work={b.work} size="lg" />
          <span class="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink-900/75 px-2.5 py-1 text-[11px] font-semibold text-white">🖼 Schimbă coperta</span>
        </button>
        <div class="min-w-0 flex-1 space-y-1.5 text-sm">
          <div class="font-serif text-lg font-bold leading-snug text-ink-900">{b.work.title}</div>
          <div class="text-ink-700">{b.work.authors || 'Autor necunoscut'}</div>
          <div class="flex flex-wrap gap-1.5 pt-1">
            {b.work.category && <span class="rounded-md bg-cream-200 px-2 py-0.5 text-xs">{b.work.category}</span>}
            {b.work.year && <span class="rounded-md bg-cream-200 px-2 py-0.5 text-xs">{b.work.year}</span>}
            {b.work.publisher && <span class="rounded-md bg-cream-200 px-2 py-0.5 text-xs">{b.work.publisher}</span>}
          </div>
          {b.work.isbn && <div class="text-xs text-ink-500">ISBN {b.work.isbn}</div>}
          <div class="pt-2 flex flex-wrap gap-2">
            <button class="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setEditWork(true)}>✎ Editează</button>
            <button class="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setCoverOpen(true)}>🖼 Coperta</button>
            <button class="btn-ghost !px-3 !py-1.5 text-xs" onClick={loadHistory}>🕘 Istoric</button>
          </div>
        </div>
      </div>

      {b.work.cover_source === 'auto' && (
        <div class="mt-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="auto-cover-banner">
          <span class="flex-1">Copertă aleasă automat. E cea corectă?</span>
          <button class="btn-ghost !px-3 !py-1 text-xs" onClick={() => { void updateWork(b.work.id, { cover_source: 'verified' }); toast('Mulțumesc! Copertă confirmată'); }}>✓ Da</button>
          <button class="btn-primary !px-3 !py-1 text-xs" onClick={() => setCoverOpen(true)}>Schimbă</button>
        </div>
      )}

      {/* lectura mea */}
      <section class="mt-5 card p-4">
        <div class="label">Lectura mea</div>
        <div class="grid grid-cols-4 gap-1 rounded-xl bg-cream-200 p-1" role="radiogroup" aria-label="Statusul meu">
          {STATUS_ORDER.map(st => (
            <button key={st} role="radio" aria-checked={status === st}
              class={`rounded-lg px-1 py-2 text-[12px] font-semibold ${status === st ? 'bg-white text-terra-600 shadow-sm' : 'text-ink-500'}`}
              onClick={() => setStatus(st)}>{READING_LABELS[st]}</button>
          ))}
        </div>
        {status === 'citit' && (
          <div class="mt-3 space-y-3">
            <div class="flex items-center justify-between">
              <Stars value={my?.rating || 0} onChange={v => setReading(b.work.id, { rating: v })} />
              <input type="date" class="field !w-auto !py-1.5 text-sm" value={my?.finished_at || ''} aria-label="Terminată la"
                onChange={e => { const v = (e.target as HTMLInputElement).value; void setReading(b.work.id, { finished_at: v || null, read_year: v ? Number(v.slice(0, 4)) : my?.read_year ?? null }); }} />
            </div>
            <textarea class="field min-h-[70px] text-sm" placeholder="Notițe, citate preferate…" value={my?.notes || ''}
              onBlur={e => { const v = (e.target as HTMLTextAreaElement).value; if (v !== (my?.notes || '')) void setReading(b.work.id, { notes: v }); }} />
          </div>
        )}
        {status === 'in_curs' && my?.started_at && <div class="mt-2 text-xs text-ink-500">Începută pe {my.started_at}</div>}
      </section>

      {otherReaders.length > 0 && (
        <section class="mt-4">
          <div class="label">În familie</div>
          <div class="flex flex-wrap gap-2">
            {otherReaders.map(r => {
              const m = s.view.members.get(r.member_id);
              return <span key={r.id} class="inline-flex items-center gap-1.5 rounded-full bg-white border border-cream-300 pl-1 pr-2.5 py-1 text-xs">
                <Avatar member={m} size={20} /> {m?.name}: <b>{READING_LABELS[r.status]}</b>{r.rating ? ` · ${'★'.repeat(r.rating)}` : ''}
              </span>;
            })}
          </div>
        </section>
      )}

      {synopsis && (
        <section class="mt-5">
          <div class="label">Sinopsis</div>
          <p class={`whitespace-pre-line text-[15px] leading-relaxed text-ink-700 ${expanded ? '' : 'line-clamp-6'}`}>{synopsis}</p>
          {synopsis.length > 400 && <button class="mt-1 text-sm font-semibold text-terra-600" onClick={() => setExpanded(!expanded)}>{expanded ? 'Mai puțin' : 'Citește tot'}</button>}
        </section>
      )}

      {/* exemplare */}
      <section class="mt-5">
        <div class="flex items-center justify-between">
          <div class="label">Exemplare ({b.allCopies.length})</div>
          <button class="text-sm font-semibold text-terra-600" onClick={() => setAddCopy(true)}>+ Am și eu</button>
        </div>
        <ul class="space-y-2">
          {b.allCopies.map(c => {
            const owner = c.owner_id ? s.view.members.get(c.owner_id) : null;
            const mine = c.owner_id === me?.id || isAdmin;
            const loan = b.activeLoans.find(l => l.copy_id === c.id && l.status === 'aprobat');
            const borrower = loan ? (loan.to_id ? s.view.members.get(loan.to_id)?.name : loan.to_name) : null;
            return (
              <li key={c.id} class="card p-3 flex items-center gap-3" data-testid="copy">
                <Avatar member={owner} size={32} />
                <div class="min-w-0 flex-1 text-sm">
                  <div class="font-semibold">{owner?.name || '—'}{c.shelf ? <span class="font-normal text-ink-500"> · 📍 {c.shelf}</span> : null}{c.box ? <span class="font-normal text-ink-500"> · 📦 {c.box}</span> : null}</div>
                  <div class="text-xs text-ink-500">
                    {DESTINATION_LABELS[c.destination]}{c.destination === 'copii' && c.age_group ? ` (${c.age_group})` : ''} · {COPY_STATUS[c.status] || c.status} · {c.condition}
                    {c.due_date ? ` · termen ${c.due_date}` : ''}
                    {borrower ? ` · la ${borrower}` : ''}
                  </div>
                </div>
                {mine && <button class="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setEditCopy(c)}>Editează</button>}
              </li>
            );
          })}
        </ul>
      </section>

      {history && (
        <section class="mt-5">
          <div class="label">Istoric</div>
          <ul class="text-xs text-ink-500 space-y-1">
            {history.length === 0 && <li>Fără modificări înregistrate.</li>}
            {history.map(h => <li key={h.id}>{h.at} · <b>{h.member || 'import'}</b> · {h.op} {h.fields && h.fields !== '{}' ? Object.keys(JSON.parse(h.fields)).join(', ') : ''}</li>)}
          </ul>
        </section>
      )}

      {canDelete && (
        <div class="mt-8 mb-2 text-center">
          <button class="text-sm text-red-700 underline" onClick={async () => {
            if (!confirm(`Ștergi „${b.work.title}” din catalog, cu toate exemplarele?`)) return;
            await deleteWork(b.work.id); toast('Carte ștearsă'); onClose();
          }}>Șterge cartea din catalog</button>
        </div>
      )}

      {coverOpen && <CoverPicker work={b.work} onClose={() => setCoverOpen(false)} />}
      {editWork && <EditWorkSheet key={b.work.id} work={b.work} onClose={() => setEditWork(false)} />}
      {editCopy && <EditCopySheet key={editCopy.id} copy={editCopy} onClose={() => setEditCopy(null)} />}
      {addCopy && <AddCopySheet work={b.work} onClose={() => setAddCopy(false)} />}
    </Sheet>
  );
}
